import * as types from './actionTypes';
import { IMaintenanceTrigger, ThunkResult } from '../models';
import { msalFetch } from '../components/auth/Auth-Utils';
import API from '../constants/apiEndpoints';
import { appSyncItemNames } from '../constants/constants';
import { AppSyncItemStatus, measurementPointTypesEnum } from '../models-enums';
import { findUnseededMaintenanceTriggers, MaintenanceTriggerValidationContext } from './maintenanceTriggerUtil';

/**
 * Up-front, facility-scoped trigger pull for the job-open sync. Loads a trimmed snapshot for every
 * install base at the facility (grouped into the saved slice) so the editor renders/falls back
 * offline without a per-asset fetch. Reports progress as an initial-app-sync item.
 */
export function getMaintenanceTriggersForFacility(facilityID: string): ThunkResult<any> {
    return dispatch => {
        const url = `${API.GET.maintenanceTrigger.getByFacilitySync}?facilityID=${facilityID}`;

        dispatch({
            type: types.UPDATE_INITIAL_APP_SYNC_ITEM_STATUS,
            name: appSyncItemNames.MaintenanceTriggers,
            status: AppSyncItemStatus.inProgress
        });

        return msalFetch(url, { url, method: 'get' } as any)
            .then((data: any) => {
                dispatch({
                    type: types.GET_MAINTENANCE_TRIGGERS_BY_FACILITY_SUCCESS,
                    triggers: data.data
                });
                dispatch({ type: types.REMOVE_INITIAL_APP_SYNC_ITEM, name: appSyncItemNames.MaintenanceTriggers });
            })
            .catch((error: any) => {
                console.error('getMaintenanceTriggersForFacility', error);
                dispatch({
                    type: types.UPDATE_INITIAL_APP_SYNC_ITEM_STATUS,
                    name: appSyncItemNames.MaintenanceTriggers,
                    status: AppSyncItemStatus.failed
                });
            });
    };
}

/**
 * Stage a trigger edit locally (no API call). Pending triggers are persisted as part
 * of the Save & Log MPLR payload — see submitMeasurementPointResult.
 */
export function stageMaintenanceTrigger(trigger: IMaintenanceTrigger) {
    return {
        type: types.STAGE_MAINTENANCE_TRIGGER,
        trigger
    };
}

/**
 * After Save & Log dispatches the MPLR (which carries the triggers in its payload),
 * move pending edits into the saved-by-server slice and clear pending. The actual
 * server IDs will be reconciled the next time the facility trigger sync runs.
 */
export function commitMaintenanceTriggersForInstallBase(installBaseID: string) {
    return {
        type: types.COMMIT_PENDING_MAINTENANCE_TRIGGERS,
        installBaseID
    };
}

/**
 * Save & Log guard: returns the offending maintenance types (empty array = valid) for an install
 * base where an enabled, never-maintained trigger is being logged with no maintenance recorded and
 * no escape valve (see isMaintenanceTriggerUnseeded). Only maintenance MPs on the list being saved
 * (listTabIDs) are checked: triggers synced for the asset from other lists have no control on
 * screen, so blocking on them would strand the tech (MMG-4430). The caller
 * (MeasurementPointListForm.handleSubmit) shows the toastr and switches to the offending trigger's
 * tab. Skipped on Cannot Complete.
 */
export function getUnseededMaintenanceTriggersForInstallBase(
    installBaseID: string,
    listTabIDs: Set<string>
): ThunkResult<number[]> {
    return (_dispatch, getState) => {
        const state = getState();
        const saved = state.maintenanceTriggers.saved[installBaseID] || [];
        const pendingByType = state.maintenanceTriggers.pending[installBaseID] || {};
        const answers = state.measurementPointResults.selectedResult.measurementPointAnswers || [];
        const mpsByID = state.measurementPointLists.measurementPointsByID;

        const contexts: MaintenanceTriggerValidationContext[] = [];
        const seenTypes = new Set<number>();
        Object.values(mpsByID).forEach(mp => {
            const maintenanceType = mp.maintenanceType;
            if (
                mp.type !== measurementPointTypesEnum.maintenance ||
                maintenanceType === undefined ||
                mp.isDeleted ||
                !listTabIDs.has(mp.measurementPointTabID) ||
                seenTypes.has(maintenanceType)
            ) {
                return;
            }
            seenTypes.add(maintenanceType);
            contexts.push({
                maintenanceType,
                pending: pendingByType[maintenanceType],
                answer: answers.find(a => a.measurementPointID === mp.id),
                synced: saved.find(t => t.maintenanceType === maintenanceType)
            });
        });

        return findUnseededMaintenanceTriggers(contexts);
    };
}
